/**
 * Error Helper Utilities
 * Provides consistent error message extraction from API responses
 */

/**
 * Extracts a user-friendly error message from various API error response formats
 * @param error - The error object from API response or catch block
 * @param defaultMessage - Default message if no specific error message is found
 * @returns A user-friendly error message
 */
export function extractErrorMessage(error: any, defaultMessage: string = 'An error occurred'): string {
  // Check axios response error structure
  if (error?.response?.data) {
    const errorData = error.response.data;
    
    // Handle nested error object
    if (errorData.error) {
      // New format: { error: { error: "user_exists", error_description: "..." } }
      if (typeof errorData.error === 'object') {
        if (errorData.error.error_description) {
          return errorData.error.error_description;
        }
        if (errorData.error.message) {
          return errorData.error.message;
        }
      }
      // String error
      if (typeof errorData.error === 'string') {
        return errorData.error;
      }
    }
    
    // Check root-level error fields
    if (errorData.error_description) {
      return errorData.error_description;
    }
    if (errorData.message) {
      return errorData.message;
    }
    if (errorData.description) {
      return errorData.description;
    }
  }
  
  // Check if error is an Error object with message
  if (error?.message) {
    return error.message;
  }
  
  // Check if error is a string
  if (typeof error === 'string') {
    return error;
  }
  
  return defaultMessage;
}

/**
 * Formats error message for display in UI
 * @param error - The error object
 * @param operation - The operation that failed (e.g., "create invitation", "verify token")
 * @returns Formatted error message
 */
export function formatErrorMessage(error: any, operation: string): string {
  const message = extractErrorMessage(error);
  
  // Check for specific error types and provide more context
  if (message.toLowerCase().includes('user_exists') || message.toLowerCase().includes('already exists')) {
    return `Cannot ${operation}: ${message}`;
  }
  
  if (message.toLowerCase().includes('expired')) {
    return `Cannot ${operation}: ${message}. Please request a new invitation.`;
  }
  
  if (message.toLowerCase().includes('unauthorized') || message.toLowerCase().includes('forbidden')) {
    return `Permission denied: ${message}`;
  }
  
  if (message.toLowerCase().includes('network') || message.toLowerCase().includes('timeout')) {
    return `Network error: ${message}. Please check your connection and try again.`;
  }
  
  return message;
}

/**
 * Checks if error is due to duplicate resource
 * @param error - The error object
 * @returns true if error is due to duplicate resource
 */
export function isDuplicateError(error: any): boolean {
  const message = extractErrorMessage(error, '').toLowerCase();
  return message.includes('exists') || 
         message.includes('duplicate') || 
         message.includes('already');
}

/**
 * Checks if error is due to expired resource
 * @param error - The error object
 * @returns true if error is due to expired resource
 */
export function isExpiredError(error: any): boolean {
  const message = extractErrorMessage(error, '').toLowerCase();
  return message.includes('expired') || 
         message.includes('timeout');
}

/**
 * Checks if error is due to authentication/authorization
 * @param error - The error object
 * @returns true if error is auth-related
 */
export function isAuthError(error: any): boolean {
  const message = extractErrorMessage(error, '').toLowerCase();
  const statusCode = error?.response?.status;
  
  return statusCode === 401 || 
         statusCode === 403 ||
         message.includes('unauthorized') || 
         message.includes('forbidden') ||
         message.includes('not authenticated') ||
         message.includes('not authorized');
}